import { Flex, Text, Box, IconButton } from "@chakra-ui/react";
import { RImage, RModal } from "components";
import { IconCheckeredSg, IconDoubleArrow, IconEmptySet, IconFlame, IconForecastGg, IconInfinityGradient, IconInfinitySg, IconInfoSilver, IconRuler, IconStatsUpgrade, IconUpgrade, IconUpgradeNew } from "icons";
import { conditionsConfig } from "components/Conditions/conditions.config";
import { useState, useCallback } from "react";
import ImpactTag from "components/ImpactTag/ImpactTag";
import RaceLevel from "components/Races2/RaceLevel/RaceLevel";
import { raceLevelGradient, rarityGradients } from "utils/themeConstants";
import CheckedTag from "components/CheckedTag/CheckedTag";


// Define a type for carousel slides
interface SlideData {
  raceLevel: number;
  raceLimit: number;
  trackLevels: number[];
  stats: string[];
}

interface ForecastModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

const defaultSlides: SlideData[] = [
  {
    raceLevel: 1,
    raceLimit: 20,
    trackLevels: [1, 2],
    stats: ["None"],
  },
  {
    raceLevel: 2,
    raceLimit: 450,
    trackLevels: [1, 2],
    stats: ["Speed", "Fast Turns"],
  },
  {
    raceLevel: 3,
    raceLimit: 450,
    trackLevels: [1, 2, 3],
    stats: ["Speed", "Fast Turns", "Handling"],
  },
  {
    raceLevel: 4,
    raceLimit: 450,
    trackLevels: [1, 2, 3, 4],
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns"],
  },
  {
    raceLevel: 5,
    raceLimit: 450,
    trackLevels: [1, 2, 3, 4, 5],
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns", "Cooling", "Braking"],
  },
  {   
    raceLevel: 6,
    raceLimit: 450,
    trackLevels: [1, 2, 3, 4, 5, 6],
    stats: ["Speed", "Fast Turns", "Handling", "Slow Turns", "Cooling", "Braking", "Acceleration", "Stability"],
  } 
]

const ForecastModal = ({ 
  isOpen, 
  onClose,
}: ForecastModalProps) => {
  const [slides] = useState<SlideData[]>(defaultSlides);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const currentCondition = slides[currentSlide];

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title="CAR LEVELS"
      icon={<IconUpgradeNew />}
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        <Text
          fontSize={{ base: '0.75rem', md: '0.875rem'}}
          lineHeight={{ base: '0.875rem', md: '1.125rem'}}
          fontWeight={500}
          color="white.80"
        >
          Car Levels determine your car’s access to tracks and stats—upgrade or fuse to unlock more.
        </Text>

        {/* Carousel slide */}
        <Flex
          position="relative"
          w="full"
          borderRadius="0.5rem"
          background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
          p="1rem"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.06) 100%)',
            borderRadius: '0.5rem',
            padding: '2px',
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            maskComposite: "exclude",
            zIndex: -1,
          }}
          flexDir="column"
          gap="0.5"
        >
          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)"
            w="180px"
            h="100px"
            borderRadius="0.1rem"
            border="1px solid rgba(255, 255, 255, 0.1)"
            gap="2"
            alignItems="flex-start"
            p="1"
            position="relative"
            flexDir="column"
            mb="0.5rem"
          >
            <Box 
              position="absolute"
              inset={0}
              background={raceLevelGradient[currentCondition.raceLevel as keyof typeof raceLevelGradient]}
              borderRadius="0.1rem"
              zIndex={0}
            />
            
            <RaceLevel 
              level={currentCondition.raceLevel}
            />
            <RImage
              src={require(`public/images/cars/blank-livery.png`)}
              alt="Car"
              width="0"
              height="0"
              style={{ 
                width: '100%', 
                height: '100%',
                position: 'absolute',
                inset: 0,
                objectFit: 'cover',
                borderRadius: '0.1rem',
               }}
            />
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            py="0.5rem"
            px="0.75rem"
            w="full"
            borderRadius="0.25rem"
            gap="2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="2">
              <IconCheckeredSg />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={500}
                color="white.80"
              >
                Race Limit
              </Text>
            </Flex>

            <Text
              fontSize={{ base: '0.875rem', md: '1.125rem'}}
              lineHeight={{ base: '1.125rem', md: '1.5rem'}}
              fontWeight={400}
              color="treasuredTeal.100"
            >
              {currentCondition.raceLimit}
            </Text>
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            py="0.5rem"
            px="0.75rem"
            w="full"
            borderRadius="0.25rem"
            gap="2"
            alignItems="center"
            justifyContent="space-between"
          >
            <Flex alignItems="center" gap="2">
              <IconInfinitySg />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={500}
                color="white.80"
                ml="-1"
              >
                Tracks
              </Text>
            </Flex>

            <Flex gap="2">
              {currentCondition.trackLevels.map((trackLevel, index) => (
                <Flex key={`track-level-${index}`}>
                  <RaceLevel
                    level={trackLevel}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>

          <Flex
            background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
            p="0.5rem"
            w="full"
            borderRadius="0.25rem"
            flexDir="column"
            gap="3"
          >
            <Flex alignItems="center" gap="0.5rem" pl="0.25rem">
              <IconStatsUpgrade 
                __css={{
                  path: {
                    fill: 'white.100',
                  }
                }}
              />

              <Text
                fontSize={{ base: '0.875rem', md: '1.125rem'}}
                lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                fontWeight={600}
                color="white.80"
              >
                Available Stats
              </Text>
            </Flex>

            <Flex pl="0.25rem" flexWrap="wrap" gap="2">
              {slides[currentSlide].stats.map((stat, index) => (
                currentSlide === 0 ? (
                  <Flex
                    key={`stat-${index}`}
                    background="linear-gradient(90.65deg, rgba(189, 189, 189, 0) 0.56%, rgba(189, 189, 189, 0.24) 99.44%)"
                    borderRightRadius="0.25rem"
                    position="relative"
                    px="8px"
                    py="6px"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(90.65deg, rgba(189, 189, 189, 0) 0.56%, rgba(189, 189, 189, 0.24) 99.44%)',
                      borderRadius: '0.25rem',
                      padding: '2px',
                      paddingLeft: '0',
                      mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",   
                      maskComposite: "exclude",
                    }}
                    gap="1"
                  >
                    <IconEmptySet w="1rem" h="auto" />

                    <Text
                      fontSize={{ base: '0.75rem', md: '0.875rem'}}
                      lineHeight={{ base: '0.875rem', md: '1.125rem'}}
                      fontWeight={500}
                      color="cerebralGrey.100"
                    >
                      None
                    </Text>
                  </Flex>
                ) : (
                  <CheckedTag title={stat} key={`stat-${index}`} />
                )
              ))}
            </Flex>
          </Flex>
        </Flex>

        {/* Carousel pagination and navigation */}
        <Flex justify="center" align="center" mt="2" gap="2">
          {/* Previous button */}
          <IconButton
            aria-label="Previous slide"
            icon={<IconDoubleArrow transform="rotate(180deg)" />}
            onClick={handlePrevious}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === 0}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
            minW="auto"
            h="auto"
          />

          {/* Pagination dots */}
          <Flex gap="2">
            {slides.map((_, index) => (
              <Box
                key={`dot-${index}`}
                w="0.75rem"
                h="0.75rem"
                borderRadius="full"
                bg={currentSlide === index ? "white" : "rgba(255, 255, 255, 0.3)"}
                cursor="pointer"
                onClick={() => goToSlide(index)}
              />
            ))}
          </Flex>

          {/* Next button */}
          <IconButton
            aria-label="Next slide"
            icon={<IconDoubleArrow />}
            onClick={handleNext}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === slides.length - 1}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            minW="auto"
            h="auto"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
          />
        </Flex>
      </Flex>
    </RModal>
  );
};

export default ForecastModal;